import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CaretDown, MagnifyingGlass, MapPin, CircleNotch, X, CheckCircle,
} from "@phosphor-icons/react";
import { fetchGeoHierarchy, searchPlz } from "@/lib/api";

/**
 * LocationPicker — unified picker für Österreich/Deutschland.
 *
 * Modi (in einem Widget):
 *  1. Direkt-Suche: PLZ, Ort, Straße → ruft /api/geo/plz (Nominatim-Proxy)
 *  2. Hierarchisch: Land → Bundesland → Landkreis/Bezirk → ruft /api/geo/hierarchy
 *
 * Callback: onPick({ source, label, lon, lat, loc_id?, country, bundesland? })
 *   source = "plz" | "location"
 */
export default function LocationPicker({
  onPick,
  placeholder = "PLZ, Ort, Adresse oder Landkreis suchen…",
  testid = "location-picker",
  defaultCountry = null,
  compact = false,
}) {
  const [mode, setMode] = useState("search"); // search | browse
  const [q, setQ] = useState("");
  const [country, setCountry] = useState(defaultCountry); // null|"at"|"de"
  const [plzResults, setPlzResults] = useState([]);
  const [plzLoading, setPlzLoading] = useState(false);
  const [hier, setHier] = useState(null);
  const [expandedCountry, setExpandedCountry] = useState(null);
  const [expandedState, setExpandedState] = useState(null);
  const [picked, setPicked] = useState(null);
  const debounce = useRef(null);

  // Load hierarchy lazy once
  useEffect(() => {
    if (mode === "browse" && !hier) {
      fetchGeoHierarchy()
        .then((d) => setHier(d))
        .catch(() => setHier({ tree: [], total: 0 }));
    }
  }, [mode, hier]);

  // PLZ/address search debounced
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!q || q.trim().length < 2) {
      setPlzResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      setPlzLoading(true);
      try {
        const results = await searchPlz(q.trim(), country || undefined);
        setPlzResults(results);
      } catch {
        setPlzResults([]);
      } finally {
        setPlzLoading(false);
      }
    }, 400);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [q, country]);

  const handlePickPlz = (r) => {
    const label =
      r.address?.postcode && r.address?.city
        ? `${r.address.postcode} ${r.address.city}`
        : (r.display_name || "").split(",").slice(0, 2).join(",");
    const pickData = {
      source: "plz",
      label: label.trim(),
      lon: r.lon,
      lat: r.lat,
      country: r.address?.country_code || null,
      bundesland: r.address?.state || null,
      postcode: r.address?.postcode || null,
    };
    setPicked(pickData);
    setQ(label);
    setPlzResults([]);
    onPick?.(pickData);
  };

  const handlePickLocation = (country_code, bl, loc) => {
    const pickData = {
      source: "location",
      loc_id: loc.id,
      label: loc.label,
      lon: loc.lon,
      lat: loc.lat,
      country: country_code,
      bundesland: bl,
    };
    setPicked(pickData);
    onPick?.(pickData);
  };

  const clear = () => {
    setPicked(null);
    setQ("");
    setPlzResults([]);
  };

  const tree = hier?.tree || [];
  const totalLocs = hier?.total || 0;

  return (
    <div className="w-full" data-testid={testid}>
      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-2" role="tablist">
        <button
          role="tab"
          onClick={() => setMode("search")}
          data-testid={`${testid}-tab-search`}
          className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
            mode === "search"
              ? "bg-[#00FA9A] text-black"
              : "bg-white/5 text-zinc-400 hover:bg-white/10"
          }`}
        >
          <MagnifyingGlass size={12} className="inline -mt-0.5 mr-1" />
          PLZ / Ort suchen
        </button>
        <button
          role="tab"
          onClick={() => setMode("browse")}
          data-testid={`${testid}-tab-browse`}
          className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
            mode === "browse"
              ? "bg-[#00FA9A] text-black"
              : "bg-white/5 text-zinc-400 hover:bg-white/10"
          }`}
        >
          <MapPin size={12} className="inline -mt-0.5 mr-1" />
          Aus Liste wählen
        </button>
        {hier && (
          <span className="text-[10px] text-zinc-500 ml-auto font-mono">
            {totalLocs} Regionen
          </span>
        )}
      </div>

      {/* SEARCH MODE */}
      {mode === "search" && (
        <div>
          <div className="flex items-center gap-2 bg-[#121212] border border-white/10 rounded-sm px-3 py-2 focus-within:border-[#00FA9A]/40">
            {plzLoading ? (
              <CircleNotch size={14} className="animate-spin text-zinc-500" />
            ) : (
              <MagnifyingGlass size={14} className="text-zinc-500" />
            )}
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              data-testid={`${testid}-input`}
              className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-zinc-500"
            />
            {q && (
              <button
                onClick={clear}
                aria-label="Leeren"
                data-testid={`${testid}-clear`}
                className="text-zinc-500 hover:text-white"
              >
                <X size={12} />
              </button>
            )}
          </div>
          {/* Country filter pills */}
          <div className="flex items-center gap-1 mt-2">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider mr-1">
              Filter:
            </span>
            {["", "at", "de"].map((c) => (
              <button
                key={c || "all"}
                onClick={() => setCountry(c || null)}
                data-testid={`${testid}-country-${c || "all"}`}
                className={`px-2 py-0.5 text-[11px] rounded-sm ${
                  (country || "") === c
                    ? "bg-[#00FA9A] text-black"
                    : "bg-white/5 text-zinc-400 hover:bg-white/10"
                }`}
              >
                {c === "" ? "Alle" : c === "at" ? "🇦🇹 AT" : "🇩🇪 DE"}
              </button>
            ))}
          </div>

          {/* Results */}
          {plzResults.length > 0 && (
            <div
              className="mt-2 border border-white/10 rounded-sm bg-[#121212] max-h-64 overflow-y-auto"
              data-testid={`${testid}-results`}
            >
              {plzResults.map((r, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePickPlz(r)}
                  data-testid={`${testid}-result-${idx}`}
                  className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/10 last:border-b-0 flex items-start gap-2"
                >
                  <MapPin size={12} className="shrink-0 mt-0.5 text-[#00FA9A]" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">
                      {r.address?.postcode && (
                        <span className="font-mono text-[#00FA9A] mr-1">
                          {r.address.postcode}
                        </span>
                      )}
                      {r.address?.city || r.display_name.split(",")[0]}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">
                      {r.display_name.split(",").slice(1, 4).join(", ")}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {q.length >= 2 && !plzLoading && plzResults.length === 0 && (
            <div className="mt-2 text-xs text-zinc-500 px-3 py-2 bg-white/5 rounded-sm">
              Keine Treffer. Probier „PLZ + Ort" (z. B. „4020 Linz") oder
              wechsle zu <button onClick={() => setMode("browse")} className="underline">„Aus Liste wählen"</button>.
            </div>
          )}
        </div>
      )}

      {/* BROWSE MODE */}
      {mode === "browse" && (
        <div className="border border-white/10 rounded-sm bg-[#121212] max-h-96 overflow-y-auto" data-testid={`${testid}-browse`}>
          {!hier && (
            <div className="p-3 text-xs text-zinc-500 flex items-center gap-2">
              <CircleNotch size={12} className="animate-spin" /> Lade Regionen …
            </div>
          )}
          {tree.map((c) => (
            <div key={c.code}>
              <button
                onClick={() =>
                  setExpandedCountry(expandedCountry === c.code ? null : c.code)
                }
                data-testid={`${testid}-country-row-${c.code}`}
                className="w-full text-left px-3 py-2 bg-white/5 hover:bg-white/10 font-medium text-sm flex items-center justify-between border-t border-white/10 first:border-t-0"
              >
                <span>
                  {c.code === "AT" ? "🇦🇹" : "🇩🇪"} {c.label}
                  <span className="text-[10px] font-mono text-zinc-500 ml-2">
                    {c.states.reduce((a, s) => a + s.locations.length, 0)} Orte
                  </span>
                </span>
                <CaretDown
                  size={12}
                  className={`transition-transform ${expandedCountry === c.code ? "rotate-180" : ""}`}
                />
              </button>
              {expandedCountry === c.code &&
                c.states.map((s) => {
                  const stateKey = `${c.code}-${s.name}`;
                  const isOpen = expandedState === stateKey;
                  return (
                    <div key={stateKey}>
                      <button
                        onClick={() =>
                          setExpandedState(isOpen ? null : stateKey)
                        }
                        data-testid={`${testid}-state-${stateKey}`}
                        className="w-full text-left pl-6 pr-3 py-1.5 bg-[#121212] hover:bg-white/5 text-xs text-white flex items-center justify-between border-t border-white/10"
                      >
                        <span>
                          {s.name}
                          <span className="text-[10px] font-mono text-zinc-500 ml-2">
                            {s.locations.length}
                          </span>
                        </span>
                        <CaretDown
                          size={10}
                          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isOpen && (
                        <div className="bg-[#161616]">
                          {s.locations.map((loc) => (
                            <button
                              key={loc.id}
                              onClick={() => handlePickLocation(c.code, s.name, loc)}
                              data-testid={`${testid}-loc-${loc.id}`}
                              className={`w-full text-left pl-10 pr-3 py-1 text-xs hover:bg-[#00FA9A]/10 ${
                                picked?.loc_id === loc.id ? "bg-[#00FA9A]/10 text-[#00FA9A] font-medium" : "text-white"
                              }`}
                            >
                              {loc.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      )}

      {/* Picked summary */}
      {picked && !compact && (
        <div
          className="mt-3 px-3 py-2 bg-[#00FA9A]/10 border border-[#00FA9A]/40 rounded-sm flex items-start gap-2"
          data-testid={`${testid}-picked`}
        >
          <CheckCircle size={14} className="text-[#00FA9A] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#00FA9A] truncate">
              {picked.label}
            </div>
            <div className="text-[10px] font-mono text-zinc-400">
              {picked.country} {picked.bundesland ? `· ${picked.bundesland}` : ""} · {picked.lat.toFixed(4)}, {picked.lon.toFixed(4)}
            </div>
          </div>
          <button
            onClick={clear}
            className="text-zinc-400 hover:text-white"
            data-testid={`${testid}-picked-clear`}
            aria-label="Auswahl zurücksetzen"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
